import { Resolver, Mutation, Arg, Ctx, Directive } from 'type-graphql'
import { In } from 'typeorm'
import { getRepository } from '@things-factory/shell'

import { KpiOrgScope } from './kpi-org-scope'
import { NewKpiOrgScope, KpiOrgScopePatch } from './kpi-org-scope-type'

@Resolver(KpiOrgScope)
export class KpiOrgScopeMutation {
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Directive('@transaction')
  @Mutation(returns => KpiOrgScope, { description: 'Create a new KPI org-scope mapping with the provided details.' })
  async createKpiOrgScope(
    @Arg('kpiOrgScope', { description: 'Input object containing details for the new org-scope mapping.' })
    kpiOrgScope: NewKpiOrgScope,
    @Ctx() context: ResolverContext
  ): Promise<KpiOrgScope> {
    const { domain, user, tx } = context.state

    // Check if org already exists for this domain
    const existingOrgScope = await getRepository(KpiOrgScope, tx).findOne({
      where: { domain: { id: domain.id }, org: kpiOrgScope.org }
    })

    if (existingOrgScope) {
      throw new Error(`Organization '${kpiOrgScope.org}' already has a scope mapping in this domain`)
    }

    const entity: Partial<KpiOrgScope> = {
      ...kpiOrgScope,
      domain,
      creator: user,
      updater: user
    }

    return await getRepository(KpiOrgScope, tx).save(entity)
  }

  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Directive('@transaction')
  @Mutation(returns => [KpiOrgScope], {
    description: 'Update multiple KPI org-scope mappings with the provided patches.'
  })
  async updateMultipleKpiOrgScope(
    @Arg('patches', type => [KpiOrgScopePatch], {
      description: 'Array of patch objects for updating org-scope mappings.'
    })
    patches: KpiOrgScopePatch[],
    @Ctx() context: ResolverContext
  ): Promise<KpiOrgScope[]> {
    const { domain, user, tx } = context.state

    const ids = patches.map(patch => patch.id)
    const existingOrgScopes = await getRepository(KpiOrgScope, tx).find({
      where: {
        domain: { id: domain.id },
        id: In(ids)
      }
    })

    if (existingOrgScopes.length !== patches.length) {
      throw new Error('Some org-scope mappings not found or not accessible')
    }

    const updatedOrgScopes: KpiOrgScope[] = []

    for (const patch of patches) {
      const existingOrgScope = existingOrgScopes.find(os => os.id === patch.id)
      if (!existingOrgScope) continue

      // Check if org is being changed and if new org already exists
      if (patch.org && patch.org !== existingOrgScope.org) {
        const duplicateOrgScope = await getRepository(KpiOrgScope, tx).findOne({
          where: {
            domain: { id: domain.id },
            org: patch.org,
            id: In(ids.filter(id => id !== patch.id))
          }
        })
        if (duplicateOrgScope) {
          throw new Error(`Organization '${patch.org}' already has a scope mapping in this domain`)
        }
      }

      const updatedEntity: Partial<KpiOrgScope> = {
        ...existingOrgScope,
        ...patch,
        updater: user,
        updatedAt: new Date()
      }

      const savedOrgScope = await getRepository(KpiOrgScope, tx).save(updatedEntity)
      updatedOrgScopes.push(savedOrgScope)
    }

    return updatedOrgScopes
  }

  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Directive('@transaction')
  @Mutation(returns => Boolean, { description: 'Delete KPI org-scope mappings by their IDs.' })
  async deleteKpiOrgScopes(
    @Arg('ids', type => [String], { description: 'Array of org-scope mapping IDs to delete.' }) ids: string[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state

    const existingOrgScopes = await getRepository(KpiOrgScope, tx).find({
      where: {
        domain: { id: domain.id },
        id: In(ids)
      }
    })

    if (existingOrgScopes.length === 0) {
      throw new Error('No org-scope mappings found to delete')
    }

    // Soft delete
    await getRepository(KpiOrgScope, tx).softDelete(existingOrgScopes.map(os => os.id))

    return true
  }

  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Directive('@transaction')
  @Mutation(returns => [KpiOrgScope], { description: 'Bulk create or update KPI org-scope mappings.' })
  async bulkUpsertKpiOrgScopes(
    @Arg('orgScopes', type => [NewKpiOrgScope], { description: 'Array of org-scope mapping data for bulk upsert.' })
    orgScopes: NewKpiOrgScope[],
    @Ctx() context: ResolverContext
  ): Promise<KpiOrgScope[]> {
    const { domain, user, tx } = context.state

    const orgs = orgScopes.map(os => os.org)
    const existingOrgScopes = await getRepository(KpiOrgScope, tx).find({
      where: {
        domain: { id: domain.id },
        org: In(orgs)
      }
    })

    const results: KpiOrgScope[] = []

    for (const orgScopeData of orgScopes) {
      const existing = existingOrgScopes.find(os => os.org === orgScopeData.org)

      if (existing) {
        // Update existing
        const updatedEntity: Partial<KpiOrgScope> = {
          ...existing,
          ...orgScopeData,
          updater: user,
          updatedAt: new Date()
        }
        const updated = await getRepository(KpiOrgScope, tx).save(updatedEntity)
        results.push(updated)
      } else {
        // Create new
        const newEntity: Partial<KpiOrgScope> = {
          ...orgScopeData,
          domain,
          creator: user,
          updater: user
        }
        const saved = await getRepository(KpiOrgScope, tx).save(newEntity)
        results.push(saved)
      }
    }

    return results
  }
}
